import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { readableTextOn, type InvoiceTemplate } from '@tailoring-crm/shared';

/**
 * A thumbnail of a bill layout — header band, item table, totals block — drawn in plain boxes.
 *
 * Not an image of the real PDF. Five images would go stale the first time a template moved a
 * line, and they could not take the accent being picked; boxes are honest about being a sketch
 * and repaint as the colour changes. The real thing is the live preview beside the cards.
 *
 * Paper is white in both themes, because a bill is. Classic stays grey whatever the accent:
 * that is what Classic is.
 */

const PAPER = '#ffffff';
const INK = '#525252';
const FAINT = '#d4d4d4';
const RULE = '#e5e5e5';

function Bar({
  width,
  colour,
  height = 3,
  style,
}: {
  width: string;
  colour: string;
  height?: number;
  style?: CSSProperties;
}): ReactElement {
  return (
    <div style={{ width, height, background: colour, borderRadius: 1, flexShrink: 0, ...style }} />
  );
}

/** Item-table rows: a header row in `head`, then hairline-separated rows. */
function Rows({
  count,
  head,
  gap = 5,
}: {
  count: number;
  head: string;
  gap?: number;
}): ReactElement {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }}>
      <div style={{ height: 5, background: head, borderRadius: 1 }} />
      {Array.from({ length: count }, (_, index) => (
        <div
          key={index}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            paddingBottom: 2,
            borderBottom: `1px solid ${RULE}`,
          }}
        >
          <Bar width="45%" colour={FAINT} height={2} />
          <Bar width="15%" colour={FAINT} height={2} />
        </div>
      ))}
    </div>
  );
}

/** A 1:√2 sheet with a little padding, the shape every A4 template is drawn on. */
function Sheet({ children, style }: { children: ReactNode; style?: CSSProperties }): ReactElement {
  return (
    <div
      aria-hidden="true"
      style={{
        aspectRatio: '1 / 1.414',
        width: '100%',
        background: PAPER,
        border: `1px solid ${RULE}`,
        borderRadius: 4,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function InvoiceTemplateSketch({
  template,
  accent,
}: {
  template: InvoiceTemplate;
  accent: string;
}): ReactElement {
  const onAccent = readableTextOn(accent);
  // The accent at about 15%, for table headers that should hint at the colour, not shout it.
  const tint = `${accent}26`;

  switch (template) {
    case 'modern':
      return (
        <Sheet style={{ padding: 0 }}>
          <div
            data-sketch-accent={accent}
            style={{
              background: accent,
              padding: 8,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <Bar width="50%" colour={onAccent} height={4} />
            <Bar width="30%" colour={onAccent} height={2} />
          </div>
          <div style={{ padding: '0 8px' }}>
            <Rows count={4} head={tint} />
          </div>
          <div
            style={{ marginTop: 'auto', padding: 8, display: 'flex', justifyContent: 'flex-end' }}
          >
            <div style={{ width: '45%', background: accent, borderRadius: 2, padding: 4 }}>
              <Bar width="100%" colour={onAccent} height={3} />
            </div>
          </div>
        </Sheet>
      );

    case 'minimal':
      return (
        <Sheet style={{ padding: 10, gap: 10 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <Bar width="40%" colour={accent} height={4} />
            <Bar width="25%" colour={FAINT} height={2} />
          </div>
          <div data-sketch-accent={accent} style={{ height: 1, background: accent }} />
          <Rows count={3} head={PAPER} gap={6} />
          <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
            <Bar width="35%" colour={accent} height={3} />
          </div>
        </Sheet>
      );

    case 'boutique':
      return (
        <Sheet style={{ padding: 4 }}>
          <div
            data-sketch-accent={accent}
            style={{
              flex: 1,
              border: `1.5px solid ${accent}`,
              borderRadius: 2,
              padding: 6,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
              <div style={{ width: 10, height: 10, borderRadius: '50%', background: accent }} />
              <Bar width="50%" colour={accent} height={3} />
              <Bar width="30%" colour={FAINT} height={2} />
            </div>
            <Rows count={3} head={tint} />
            <div
              style={{
                marginTop: 'auto',
                marginLeft: 'auto',
                width: '45%',
                borderTop: `1px solid ${accent}`,
                borderBottom: `1px solid ${accent}`,
                padding: '3px 0',
              }}
            >
              <Bar width="100%" colour={INK} height={2} />
            </div>
          </div>
        </Sheet>
      );

    case 'compact':
      return (
        <Sheet style={{ padding: 6, gap: 5 }}>
          <div
            data-sketch-accent={accent}
            style={{
              borderLeft: `3px solid ${accent}`,
              paddingLeft: 4,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Bar width="40%" colour={accent} height={3} />
            <Bar width="25%" colour={FAINT} height={2} />
          </div>
          <Rows count={7} head={tint} gap={3} />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Bar width="40%" colour={accent} height={3} />
          </div>
        </Sheet>
      );

    case 'classic':
    default:
      // Greyscale whatever the accent — the accent is not passed down on purpose.
      return (
        <Sheet style={{ padding: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
            <div style={{ display: 'flex', gap: 4, flex: 1 }}>
              <div style={{ width: 10, height: 10, background: FAINT, flexShrink: 0 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
                <Bar width="80%" colour={INK} height={3} />
                <Bar width="50%" colour={FAINT} height={2} />
              </div>
            </div>
            <Bar width="25%" colour={INK} height={3} />
          </div>
          <div style={{ height: 1, background: INK }} />
          <Rows count={4} head={RULE} />
          <div style={{ marginTop: 'auto', marginLeft: 'auto', width: '45%' }}>
            <div style={{ borderTop: `1px solid ${INK}`, paddingTop: 3 }}>
              <Bar width="100%" colour={INK} height={3} />
            </div>
          </div>
        </Sheet>
      );
  }
}
